import { message } from "antd";
import LayoutEnum from "./layoutEnum";

const hardcodeToken = "";

function getCookieValue(name) {
  const regex = new RegExp(`(^| )${name}=([^;]+)`)
  const match = document.cookie.match(regex)
  if (match) {
    return match[2]
  }
}

export function getWorkWiseAuthToken() {
  const searchParams = new URLSearchParams(document.location.search);
  let cookieToken = getCookieValue("token")
  let queryToken = searchParams.get("token");
  const finalToken = queryToken || cookieToken || hardcodeToken;
  return finalToken;
}

export function decodeWorkWiseToken() {
  const token = getWorkWiseAuthToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Manual "work offline" mode.
 *
 * localStorage is the source of truth so non-React code (the API guard, the
 * socket guard) can read it synchronously; redux mirrors it for rendering.
 */
const MANUAL_OFFLINE_KEY = 'mileslideManualOffline';

export const getManualOfflineMode = () => {
  try {
    return localStorage.getItem(MANUAL_OFFLINE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

export const setManualOfflineMode = (enabled) => {
  try {
    localStorage.setItem(MANUAL_OFFLINE_KEY, enabled ? 'true' : 'false');
  } catch (error) {
    console.error('Error persisting offline mode:', error);
  }
};

/** Offline for our purposes: no network OR the user chose to work offline. */
export const isEffectivelyOffline = () =>
  !window.navigator.onLine || getManualOfflineMode();

export const isStyleChanged = (existingStyle, masterStyle) => {
  for (const styleKey of Object.keys(existingStyle)) {
    let existingKeySlice = typeof existingStyle[styleKey] === 'string' ? existingStyle[styleKey].substring(0, 5) : null;
    let masterKeySlice = typeof masterStyle[styleKey] === 'string' ? masterStyle[styleKey].substring(0, 5) : null;

    if (existingKeySlice !== null && masterKeySlice !== null) {
      if (existingKeySlice !== masterKeySlice) {
        // console.log('Slice Key (Master):', styleKey, 'Substring:', existingKeySlice, masterKeySlice);
        return true;
      }
    } else if (!isNaN(parseFloat(existingStyle[styleKey]))) {
      const existingNumValue = parseFloat(existingStyle[styleKey]).toFixed(3);
      const masterNumValue = isNaN(parseFloat(existingStyle[styleKey])) ?
        parseFloat(masterStyle[styleKey]).toFixed(3) : null;
      if (masterNumValue && existingNumValue !== masterNumValue) {
        // console.log('Slice Key (Master - Number):', styleKey, 'Value:', existingNumValue, masterNumValue);
        return true;
      }
    }
  }
  return false;
};

export const modifyMasterSlideData = (masterSlideData) => {

  if (!masterSlideData) {
    return null;
  }

  let modifiedTextBoxes = {};
  let filteredTextBoxIds = masterSlideData.textBoxIds.filter((id) => !id.includes('master'));
  for (const textBoxId of filteredTextBoxIds) {
    modifiedTextBoxes[textBoxId] = {
      ...masterSlideData.textBox[textBoxId],
      styles: { ...masterSlideData.textBox[textBoxId].styles, pointerEvents: 'none' },
    };
  }

  let modifiedShapes = {};
  for (const shapeId of Object.keys(masterSlideData.shapes || {})) {
    modifiedShapes[shapeId] = {
      ...masterSlideData.shapes[shapeId],
      styles: { ...masterSlideData.shapes[shapeId].styles, pointerEvents: 'none' },
    };
  }

  let modifiedWordArt = {};
  for (const wordArtId of Object.keys(masterSlideData.wordArtTextObjects || {})) {
    modifiedWordArt[wordArtId] = {
      ...masterSlideData.wordArtTextObjects[wordArtId],
      styles: { ...masterSlideData.wordArtTextObjects[wordArtId].styles, pointerEvents: 'none' },
    };
  }

  let modifiedSmartArt = {};
  for (const smartArtId of Object.keys(masterSlideData.smartArtObjects || {})) {
    modifiedSmartArt[smartArtId] = {
      ...masterSlideData.smartArtObjects[smartArtId],
      styles: { ...masterSlideData.smartArtObjects[smartArtId].styles, pointerEvents: 'none' },
    };
  }

  let modifiedImages = {};
  for (const imgId of masterSlideData.imageIds) {
    if (masterSlideData.image[imgId]) {
      modifiedImages[imgId] = {
        ...masterSlideData.image[imgId],
        styles: { ...masterSlideData.image[imgId].styles, pointerEvents: 'none' },
      };
    }
  }
  let modifiedMasterSlideData = {
    slideBackgroundColor: masterSlideData.slideBackgroundColor,
    textBoxIds: filteredTextBoxIds,
    textBox: modifiedTextBoxes,
    imageIds: [...masterSlideData.imageIds],
    image: modifiedImages,
    shapes: modifiedShapes,
    wordArtTextObjects: modifiedWordArt,
    smartArtObjects: modifiedSmartArt,
    speakerNotes: masterSlideData.speakerNotes,
  };
  return modifiedMasterSlideData;
}

export const removeDuplicates = (slideData, masterSlideData) => {

  const newSlideData = {
    ...slideData,
    textBoxIds: slideData.textBoxIds.filter(id => !masterSlideData.textBoxIds.includes(id)),
    imageIds: slideData.imageIds.filter(id => !masterSlideData.imageIds.includes(id)),
    shapes: {},
    wordArtTextObjects: {},
    smartArtObjects: {},
    image: {},
    textBox: {}
  };


  // Add textBoxes to newSlideData
  Object.keys(slideData.textBox).forEach(textBoxId => {
    if (!masterSlideData.textBox[textBoxId]) {
      newSlideData.textBox[textBoxId] = slideData.textBox[textBoxId];
    }
  });

  // Add shapes to newSlideData
  Object.keys(slideData.shapes).forEach(shapeId => {
    if (!masterSlideData.shapes[shapeId]) {
      newSlideData.shapes[shapeId] = slideData.shapes[shapeId];
    }
  });

  Object.keys(slideData.wordArtTextObjects || {}).forEach(wordArtId => {
    if (!masterSlideData.wordArtTextObjects?.[wordArtId]) {
      newSlideData.wordArtTextObjects[wordArtId] = slideData.wordArtTextObjects[wordArtId];
    }
  });

  Object.keys(slideData.smartArtObjects || {}).forEach(smartArtId => {
    if (!masterSlideData.smartArtObjects?.[smartArtId]) {
      newSlideData.smartArtObjects[smartArtId] = slideData.smartArtObjects[smartArtId];
    }
  });

  // Add images to newSlideData
  Object.keys(slideData.image).forEach(imgId => {
    if (!masterSlideData.image[imgId]) {
      newSlideData.image[imgId] = slideData.image[imgId];
    }
  });
  return newSlideData;
};

/** User-placed images, shapes, WordArt, and SmartArt — kept when switching slide layout. */
export const preserveUserSlideElements = (slide) => {
  if (!slide) {
    return {
      imageIds: [],
      image: {},
      shapes: {},
      wordArtTextObjects: {},
      smartArtObjects: {},
    };
  }
  return {
    imageIds: Object.keys(slide.image || {}),
    image: { ...(slide.image || {}) },
    shapes: { ...(slide.shapes || {}) },
    wordArtTextObjects: { ...(slide.wordArtTextObjects || {}) },
    smartArtObjects: { ...(slide.smartArtObjects || {}) },
  };
};

/** Re-apply user content onto layout-built slide data (all layout types). */
export const mergePreservedUserElements = (slideData, currentSlide) => {
  if (!currentSlide || !slideData) return slideData;
  const preserved = preserveUserSlideElements(currentSlide);
  return {
    ...slideData,
    imageIds: preserved.imageIds,
    image: preserved.image,
    shapes: preserved.shapes,
    wordArtTextObjects: {
      ...(slideData.wordArtTextObjects || {}),
      ...preserved.wordArtTextObjects,
    },
    smartArtObjects: {
      ...(slideData.smartArtObjects || {}),
      ...preserved.smartArtObjects,
    },
  };
};

export const getUpdatedSlides = (elementData, type, existingSlides) => {
  const currentSlideId = elementData.currentSlideId;
  const currentSlide = existingSlides[currentSlideId];

  switch (type) {
    case 'text': {
      const textId = elementData.textId;
      const updatedTextboxIds = [...currentSlide.textBoxIds, textId];
      const updatedTextboxes = {
        ...currentSlide.textBox,
        [textId]: {
          delta: elementData.delta ? elementData.delta : { ops: [{ insert: '' }] },
          styles: elementData.style,
        },
      };
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...currentSlide,
          textBox: updatedTextboxes,
          textBoxIds: updatedTextboxIds,
        },
      };
    }
    case 'image': {
      const imageId = elementData.imageId;
      const imageb64 = elementData.imageb64;
      const style = elementData.style;
      const updatedImageIds = [...currentSlide.imageIds, imageId];
      const updatedImages = { ...currentSlide.image, [imageId]: { imageb64, styles: style } };
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...currentSlide,
          image: updatedImages,
          imageIds: updatedImageIds,
        },
      };
    }
    case 'shape': {
      const shapeId = elementData.shapeId;
      const updatedShapes = {
        ...currentSlide.shapes,
        [shapeId]: {
          delta: elementData.delta ? elementData.delta : { ops: [{ insert: '' }] },
          styles: elementData.style,
        },
      };
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...currentSlide,
          shapes: updatedShapes,
        },
      };
    }
    case 'wordart': {
      const { wordArtId, style, data } = elementData;
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...currentSlide,
          wordArtTextObjects: {
            ...(currentSlide.wordArtTextObjects || {}),
            [wordArtId]: { ...data, styles: style },
          },
        },
      };
    }
    case 'smartart': {
      const { smartArtId, style, data } = elementData;
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...currentSlide,
          smartArtObjects: {
            ...(currentSlide.smartArtObjects || {}),
            [smartArtId]: { ...data, styles: style },
          },
        },
      };
    }
    case 'slide': {
      const { slideId, slideData } = elementData;
      return {
        ...existingSlides,
        [slideId]: slideData,
      };
    }
    case 'backgroundColor': {
      const { slideId, backgroundColor } = elementData;
      return {
        ...existingSlides,
        [slideId]: {
          ...existingSlides[slideId],
          slideBackgroundColor: backgroundColor,
        },
      };
    }
    case 'speakerNotes': {
      const { slideId, content } = elementData;
      return {
        ...existingSlides,
        [slideId]: {
          ...existingSlides[slideId],
          speakerNotes: content,
        },
      };
    }
    default:
      return existingSlides;
  }
};
export const updateTextboxDelta = (existingSlides, elementData, type) => {
  const { currentSlideId, textBoxId, shapeId, content } = elementData;

  switch (type) {
    case 'textbox': {
      const currentTextBox = existingSlides[currentSlideId]?.textBox?.[textBoxId];
      if (!currentTextBox) {
        throw new Error(`TextBox with ID ${textBoxId} not found in slide ${currentSlideId}`);
      }
      const updatedTextBox = { ...currentTextBox, delta: content };
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          textBox: {
            ...existingSlides[currentSlideId].textBox,
            [textBoxId]: updatedTextBox,
          },
        },
      };
    }
    case 'shape': {
      const currentShape = existingSlides[currentSlideId]?.shapes?.[shapeId];
      if (!currentShape) {
        throw new Error(`Shape with ID ${shapeId} not found in slide ${currentSlideId}`);
      }
      const updatedShape = { ...currentShape, delta: content };
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          shapes: {
            ...existingSlides[currentSlideId].shapes,
            [shapeId]: updatedShape,
          },
        },
      };
    }
    case 'wordart': {
      const { wordArtId } = elementData;
      const currentWordArt = existingSlides[currentSlideId]?.wordArtTextObjects?.[wordArtId];
      if (!currentWordArt) {
        throw new Error(`WordArt with ID ${wordArtId} not found in slide ${currentSlideId}`);
      }
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          wordArtTextObjects: {
            ...existingSlides[currentSlideId].wordArtTextObjects,
            [wordArtId]: { ...currentWordArt, delta: content },
          },
        },
      };
    }
    case 'smartart': {
      const { smartArtId, nodes } = elementData;
      const currentSmartArt = existingSlides[currentSlideId]?.smartArtObjects?.[smartArtId];
      if (!currentSmartArt) {
        throw new Error(`SmartArt with ID ${smartArtId} not found in slide ${currentSlideId}`);
      }
      return {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          smartArtObjects: {
            ...existingSlides[currentSlideId].smartArtObjects,
            [smartArtId]: { ...currentSmartArt, nodes },
          },
        },
      };
    }
    default:
      throw new Error(`Unknown type: ${type}`);
  }
};
export const updateElementStyle = (existingSlides, elementData, type) => {
  const { currentSlideId, textBoxId, updatedStyle } = elementData;
  let updatedSlides;

  switch (type) {
    case 'text': {
      const updatedTextBox = {
        ...existingSlides[currentSlideId].textBox[textBoxId],
        styles: updatedStyle,
      };
      updatedSlides = {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          textBox: {
            ...existingSlides[currentSlideId].textBox,
            [textBoxId]: updatedTextBox,
          },
        },
      };
      break;
    }
    case 'image': {
      const updatedImage = {
        ...existingSlides[currentSlideId].image[textBoxId],
        styles: updatedStyle,
      };
      updatedSlides = {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          image: {
            ...existingSlides[currentSlideId].image,
            [textBoxId]: updatedImage,
          },
        },
      };
      break;
    }
    case 'shape': {
      const updatedShape = {
        ...existingSlides[currentSlideId].shapes[textBoxId],
        styles: updatedStyle,
      };
      updatedSlides = {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          shapes: {
            ...existingSlides[currentSlideId].shapes,
            [textBoxId]: updatedShape,
          },
        },
      };
      break;
    }
    case 'wordart': {
      const updatedWordArt = {
        ...existingSlides[currentSlideId].wordArtTextObjects[textBoxId],
        styles: updatedStyle,
      };
      updatedSlides = {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          wordArtTextObjects: {
            ...existingSlides[currentSlideId].wordArtTextObjects,
            [textBoxId]: updatedWordArt,
          },
        },
      };
      break;
    }
    case 'smartart': {
      const updatedSmartArt = {
        ...existingSlides[currentSlideId].smartArtObjects[textBoxId],
        styles: updatedStyle,
      };
      updatedSlides = {
        ...existingSlides,
        [currentSlideId]: {
          ...existingSlides[currentSlideId],
          smartArtObjects: {
            ...existingSlides[currentSlideId].smartArtObjects,
            [textBoxId]: updatedSmartArt,
          },
        },
      };
      break;
    }
    default:
      throw new Error(`Unknown type: ${type}`);
  }

  return updatedSlides;
};

export const deleteElement = (existingSlides, elementData, type) => {
  const { slideId, elementId } = elementData;
  const updatedSlides = JSON.parse(JSON.stringify(existingSlides)); // Deep copy to avoid mutation

  switch (type) {
    case 'textbox': {
      updatedSlides[slideId].textBoxIds = updatedSlides[slideId].textBoxIds.filter(textBox => textBox !== elementId);
      delete updatedSlides[slideId].textBox[elementId];
      break;
    }
    case 'image': {
      updatedSlides[slideId].imageIds = updatedSlides[slideId].imageIds.filter(image => image !== elementId);
      delete updatedSlides[slideId].image[elementId];
      break;
    }
    case 'shape': {
      delete updatedSlides[slideId].shapes[elementId];
      break;
    }
    case 'wordart': {
      delete updatedSlides[slideId].wordArtTextObjects[elementId];
      break;
    }
    case 'smartart': {
      delete updatedSlides[slideId].smartArtObjects[elementId];
      break;
    }
    case 'slide': {
      const slideKeys = Object.keys(updatedSlides);
      const slideIndex = slideKeys.indexOf(slideId);
      const nextIndex = slideIndex === slideKeys.length - 1 ? slideIndex - 1 : slideIndex + 1;
      if (updatedSlides.currentSlideId === slideId) {
        updatedSlides.currentSlideId = slideKeys[nextIndex] ? slideKeys[nextIndex] : "";
      }
      delete updatedSlides[slideId];
      break;
    }
    default:
      throw new Error(`Unknown type: ${type}`);
  }

  return updatedSlides;
};
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const units = ["Bytes", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  if (unitIndex === 0) {
    return `${size} ${units[unitIndex]}`;
  } else if (unitIndex === 1 && size < 50) {
    return `${parseFloat(size.toFixed(0))} ${units[unitIndex]}`;
  } else {
    return `${parseFloat(size.toFixed(2))} ${units[unitIndex]}`;
  }
};
export const filterOtherLayoutElements = (slideData, type) => {

  if (!slideData) return slideData;
  const layoutType = type.split('-')[1];
  const newSlideData = {
    ...slideData,
    textBoxIds: [],
    textBox: {},
    imageIds: [],
    image: {},
    shapes: {},
    wordArtTextObjects: {},
    smartArtObjects: {},
  };
  // Helper function to check if an element is of any layout type except the current one
  const isOtherLayoutElement = (id) => {
    const normalizedId = id.toLowerCase();
    if (normalizedId.includes('wordart') || normalizedId.includes('smartart')) {
      return false;
    }
    const result = Object.values(LayoutEnum).some(layoutValue => {
      const layout = layoutValue.split('-')[1];
      const isDifferentLayout = layout.toLocaleLowerCase() !== layoutType.toLowerCase();
      const isIncludedInId = normalizedId.includes(layout.toLowerCase());
      return isDifferentLayout && isIncludedInId;
    });
    return result;
  };

  // Filter textBoxIds and populate textBox
  newSlideData.textBoxIds = slideData.textBoxIds.filter(id => {
    if (!isOtherLayoutElement(id)) {
      newSlideData.textBox[id] = slideData.textBox[id];
      return true;
    }
    return false;
  });

  // Filter imageIds and populate image
  newSlideData.imageIds = slideData.imageIds.filter(id => {
    if (!isOtherLayoutElement(id)) {
      newSlideData.image[id] = slideData.image[id];
      return true;
    }
    return false;
  });

  // Add shapes to newSlideData
  Object.keys(slideData.shapes).forEach(shapeId => {
    if (!isOtherLayoutElement(shapeId)) {
      newSlideData.shapes[shapeId] = slideData.shapes[shapeId];
    }
  });

  Object.keys(slideData.wordArtTextObjects || {}).forEach(wordArtId => {
    if (!isOtherLayoutElement(wordArtId)) {
      newSlideData.wordArtTextObjects[wordArtId] = slideData.wordArtTextObjects[wordArtId];
    }
  });

  Object.keys(slideData.smartArtObjects || {}).forEach(smartArtId => {
    if (!isOtherLayoutElement(smartArtId)) {
      newSlideData.smartArtObjects[smartArtId] = slideData.smartArtObjects[smartArtId];
    }
  });

  return newSlideData;
};

export const getWatermarkData = async () => {
  const now = new Date();
  const currentDateTime = now.toLocaleString();

  // Get IP address
  let ip = 'N/A';
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    ip = data.ip;
  } catch (error) {
    console.error("Error fetching IP:", error);
    message.error("Could not fetch IP address");
  }

  // Get location
  let location = { lat: 'N/A', lng: 'N/A' };
  try {
    location = await new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        message.error("Geolocation not supported");
        return resolve(location);
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }),
        (error) => {
          handleGeolocationError(error);
          resolve(location); // Return default on error
        }
      );
    });
  } catch (error) {
    console.error("Location error:", error);
  }

  return {
    currentDateTime,
    ip,
    location
  };
};

const handleGeolocationError = (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      message.error("Location access denied. Please enable permissions.");
      break;
    case error.POSITION_UNAVAILABLE:
      message.error("Location information unavailable.");
      break;
    case error.TIMEOUT:
      message.error("Location request timed out.");
      break;
    default:
      message.error("Unknown location error occurred.");
  }
};

// Helper functions:

export async function checkClipboardForPasteableData() {
  // Method 1: Modern async clipboard API
  if (navigator.clipboard?.readText) {
    try {
      const text = await navigator.clipboard.readText();
      if (text) return true;
    } catch { } // Ignore permission errors in this check
  }

  // Method 2: Legacy clipboard access
  if (window.clipboardData?.getData) {
    return !!window.clipboardData.getData('text');
  }

  // Method 3: Check for image support
  if (navigator.clipboard?.read) {
    try {
      const items = await navigator.clipboard.read();
      return items.some(item =>
        item.types.some(type =>
          type.startsWith('image/') ||
          type === 'text/plain'
        )
      );
    } catch { } // Ignore permission errors
  }

  return false;
}

export const convertBlobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // The result contains the Base64 string
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const findParentWithIdKeyword = (ref, keyword, maxDepth = 5) => {
  if (!ref?.current) return null;

  let currentElement = ref.current;
  let depth = 0;

  while (currentElement && depth < maxDepth) {
    if (currentElement.id && currentElement.id.includes(keyword)) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
    depth++;
  }

  return null;
};

export const findElementType = (element) => {

  if (!element) {
    return null;
  }
  let current = element;

  while (current) {
    const id = current.id?.toLowerCase() || '';

    if (id.includes('smartart')) return 'smartart';
    if (id.includes('wordart')) return 'wordart';
    if (id.includes('shape')) return 'shape';
    if (id.includes('image')) return 'image';
    if (id.includes('text')) return 'text';

    current = current.parentElement;
  }

  return null;
};

export const processTemplateData = (serverResponse) => {
  let templateData = serverResponse?.data?.data || null;
  if (templateData?.milepadData) {
    try {
      const rawData = JSON.parse(templateData.milepadData);
      delete templateData.milepadData;
      templateData.slideData = rawData;
    } catch (error) {
      console.error('Failed to parse template milepadData:', error);
      delete templateData.milepadData;
      templateData.slideData = {};
    }
  }
  return templateData;
};

export const removeTemplateIdFromURL = (history) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete('templateId');
  const newSearch = searchParams.toString();
  history.replace({
    pathname: window.location.pathname,
    search: newSearch ? `?${newSearch}` : ''
  });
};